/* WWLP Fitout Studio controller — compact sibling of wardrobe-designer.js
   for the fitouts LP: opening size, layout + per-part tweaks, lead form.
   No door step, no 3D, no pricing, no persistence. Depends only on
   WD_CATALOGUE/WD_GEOM/WD_RENDER; never touches wardrobe-designer.js.
   The design reaches the CRM via hidden [data-ma-extra-field] inputs on the
   section's <form data-ma-lead-form> — ma-lead-forms.js (shared, unchanged)
   owns validation and the POST; this file just keeps those fields in sync. */
(function () {
  'use strict';

  function q(root, sel) { return root.querySelector(sel); }
  function qa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function clamp(n, lo, hi) { return Math.min(hi, Math.max(lo, n)); }
  function roundTo(n, step) { return Math.round(n / step) * step; }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function tpl(str, map) {
    return String(str || '').replace(/\[(\w+)\]/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : m;
    });
  }
  function findDefault(arr) {
    if (!Array.isArray(arr) || !arr.length) return null;
    for (var i = 0; i < arr.length; i++) if (arr[i] && arr[i].default) return arr[i];
    return arr[0];
  }

  function init(root) {
    if (!root || root.getAttribute('data-wfs-ready') === '1') return;
    var cat = window.WD_CATALOGUE;
    var GEOM = window.WD_GEOM;
    var REND = window.WD_RENDER;
    if (!cat || !GEOM || !REND) return; // modules missing: static shell + noscript copy stand
    root.setAttribute('data-wfs-ready', '1');

    var S = window.WFS_SETTINGS || {};
    /* Unique per section instance, so generated control ids stay distinct
       if this section is ever placed on a page twice. */
    var sectionUid = (root.getAttribute('data-wfs-section') || '').replace(/[^\w-]/g, '') ||
      Math.random().toString(36).slice(2, 8);

    var face = q(root, '[data-wfs-face]');
    var live = q(root, '[data-wfs-live]');

    /* Default state — CRM-frozen shape. Doors are never surfaced in this
       UI, so they're stubbed from the catalogue defaults purely so the
       renderer and the CRM contract keep their shape. */
    function defaultDoors(W) {
      var t = findDefault(cat.doors.types);
      var ins = findDefault(cat.doors.inserts);
      var fr = findDefault(cat.doors.frames);
      return {
        type: t ? t.value : 'sliding',
        count: GEOM.bandCount(W),
        insert: ins ? ins.value : 'mirror',
        frame: fr ? fr.value : 'matt-natural'
      };
    }
    function pickDefaultLayout(W) {
      var avail = GEOM.availableLayouts(W, cat);
      if (!avail.length) return null;
      for (var i = 0; i < avail.length; i++) if (avail[i].default) return avail[i];
      return avail[0];
    }
    function defaultInstall() {
      var o = findDefault(cat.install);
      return o ? o.value : true;
    }
    function defaultState() {
      var W = cat.opening.width.default;
      var layout = pickDefaultLayout(W);
      return {
        v: 1,
        opening: { w: W, h: cat.opening.height.default, d: cat.opening.depth.options[0].value },
        doors: defaultDoors(W),
        internals: {
          topShelf: cat.topShelf.default,
          layout: layout ? layout.id : null,
          sections: layout ? GEOM.realiseLayout(layout.recipe, W, cat) : []
        },
        install: defaultInstall(),
        notes: ''
      };
    }

    var state = defaultState();
    var ui = { selected: -1 };
    var userNotes = '';
    var pendingLayoutNote = '';

    function findLayout(id) {
      for (var i = 0; i < cat.layouts.length; i++) if (cat.layouts[i].id === id) return cat.layouts[i];
      return null;
    }

    /* normaliseSections(secs, W) — ported verbatim (behaviour) from
       wardrobe-designer.js. Guards two invariants: every section within
       its catalogue min/max, AND the set summing to W exactly. Runs after
       every layout swap, width change and per-part type swap. Do not
       simplify — an earlier version satisfied the sum by inflating a
       fixed-width tower past its max and shipped an unbuildable design. */
    function normaliseSections(secs, W) {
      if (!secs || !secs.length) return secs;

      secs.forEach(function (sec) {
        var m = cat.sections[sec.t];
        if (m) sec.w = clamp(sec.w, m.min, m.max);
      });

      function push(diff, flexOnly) {
        for (var pass = 0; pass < 2 && diff; pass++) {
          for (var i = secs.length - 1; i >= 0 && diff; i--) {
            var m = cat.sections[secs[i].t];
            if (!m) continue;
            if (flexOnly && !m.flex) continue;
            var want = clamp(secs[i].w + diff, m.min, m.max);
            diff -= (want - secs[i].w);
            secs[i].w = want;
          }
          if (flexOnly) break;
        }
        return diff;
      }
      var remaining = W - secs.reduce(function (a, s) { return a + s.w; }, 0);
      remaining = push(remaining, true);
      if (remaining) remaining = push(remaining, false);

      var guard = 0;
      while (remaining > 0 && guard++ < 12) {
        var t = 'double';
        var m = cat.sections[t];
        var add = Math.min(m.max, Math.max(m.min, remaining));
        secs.push({ t: t, w: add });
        remaining -= add;
        /* Reconcile in BOTH directions. A new bay must be at least its
           catalogue min, so adding one to cover a 300mm shortfall overshoots
           by min-300; that surplus has to come back out of the other bays.
           Only reconciling the positive case left the set summing short of
           the opening, which shipped an unbuildable design to the CRM. */
        if (remaining) remaining = push(remaining, false);
      }
      guard = 0;
      while (remaining < 0 && secs.length > 1 && guard++ < 12) {
        var dropped = secs.pop();
        remaining += dropped.w;
        remaining = push(remaining, false);
      }
      if (remaining) remaining = push(remaining, false);
      return secs;
    }

    /* Width changed: re-realise (or refit) so sections always sum to W. */
    function reflowSections() {
      var W = state.opening.w;
      var allowedCounts = GEOM.allowedDoorCounts(W, state.doors.type);
      if (allowedCounts.indexOf(state.doors.count) === -1) state.doors.count = GEOM.bandCount(W);

      var lay = state.internals.layout ? findLayout(state.internals.layout) : null;
      var snapped = false;
      if (lay && GEOM.layoutIsValid(lay, W, cat)) {
        state.internals.sections = GEOM.realiseLayout(lay.recipe, W, cat);
      } else if (state.internals.layout === null && state.internals.sections.length) {
        /* Hand-edited internals: keep the customer's parts, just refit them. */
        normaliseSections(state.internals.sections, W);
      } else {
        var next = pickDefaultLayout(W);
        if (next) {
          state.internals.layout = next.id;
          state.internals.sections = GEOM.realiseLayout(next.recipe, W, cat);
          snapped = true;
        }
      }
      if (ui.selected >= state.internals.sections.length) ui.selected = -1;
      return snapped;
    }

    /* Render — rAF gated. A hidden page never runs rAF callbacks, and a
       queued one would leave rafId set forever, silently blocking every
       later render, so paint synchronously when hidden and flush on
       visibilitychange. */
    var rafId = null;
    function scheduleRender() {
      if (typeof document !== 'undefined' && document.hidden) {
        rafId = null;
        paint();
        return;
      }
      if (rafId) return;
      rafId = requestAnimationFrame(function () {
        rafId = null;
        paint();
      });
    }
    /* Registered against the section root, not `document`, so repeated
       shopify:section:load in the theme editor cannot accumulate stale
       listeners painting into detached nodes. */
    if (typeof document !== 'undefined' && !root.__wfsVis) {
      root.__wfsVis = function () {
        if (!document.contains(root)) {
          document.removeEventListener('visibilitychange', root.__wfsVis);
          return;
        }
        if (!document.hidden) { rafId = null; paint(); }
      };
      document.addEventListener('visibilitychange', root.__wfsVis);
    }

    /* Spec text, internals only.

       WD_GEOM.describe() and WD_RENDER.ariaLabel() both narrate the door
       stub this studio has to carry for the frozen CRM contract, so they
       would tell Will (and every screen reader) a panel count, insert and
       frame colour the customer was never shown a control for. Setting
       doors.type to 'none' is not a fix either, that just says "No doors."
       These two build the same sentence shape from the internals alone. */
    function partNoun(t) {
      var m = cat.sections[t];
      return m && m.label ? m.label.toLowerCase() : String(t);
    }
    function partLine(sec, i) {
      var bits = [partNoun(sec.t), sec.w + 'mm'];
      if (typeof sec.drawers === 'number') bits.push(sec.drawers + ' drawer' + (sec.drawers === 1 ? '' : 's'));
      if (typeof sec.shelves === 'number') bits.push(sec.shelves + ' shel' + (sec.shelves === 1 ? 'f' : 'ves'));
      if (sec.hangAbove) bits.push('hanging above');
      return 'Part ' + (i + 1) + ': ' + bits.join(', ') + '.';
    }
    function describeFitout(st) {
      var parts = [];
      var op = (st && st.opening) || {};
      if (isFinite(op.w) && isFinite(op.h)) parts.push(op.w + 'mm wide, ' + op.h + ' high.');
      var lay = st.internals.layout ? findLayout(st.internals.layout) : null;
      parts.push(lay ? lay.label + ' layout.' : 'Custom layout.');
      parts.push(st.internals.topShelf ? 'Top shelf across the full width.' : 'No top shelf.');
      (st.internals.sections || []).forEach(function (sec, i) { parts.push(partLine(sec, i)); });
      return parts.join(' ').replace(/\s+/g, ' ').trim();
    }
    function fitoutAriaLabel(st) {
      var op = (st && st.opening) || {};
      var secs = (st.internals && st.internals.sections) || [];
      return 'Elevation drawing of your wardrobe fitout, ' + op.w + 'mm wide by ' + op.h +
        'mm high, ' + secs.length + ' part' + (secs.length === 1 ? '' : 's') + ': ' +
        secs.map(function (s) { return partNoun(s.t); }).join(', ') + '.';
    }

    function paint() {
      if (face) {
        face.innerHTML = REND.renderElevation(state, 'int', { showDims: true, selected: ui.selected });
        face.setAttribute('aria-label', fitoutAriaLabel(state));
      }
      syncLeadFields();
    }

    var liveTimer = null;
    function announce(msg) {
      if (!live) return;
      if (liveTimer) clearTimeout(liveTimer);
      liveTimer = setTimeout(function () { live.textContent = msg; }, 500);
    }

    /* Lead form sync — hidden data-ma-field-label inputs, plus the message
       field, kept current on every state change. Never shown to the
       customer, never priced. */
    function syncLeadFields() {
      var lay = state.internals.layout ? findLayout(state.internals.layout) : null;
      /* Mid support required rides along silently — never rendered anywhere
         in the visible UI, blank (so ma-lead-forms.js skips it) when false. */
      var flags = GEOM.midSupportFlags(state.internals.sections, cat);
      var map = {
        'Design': JSON.stringify(state),
        'Opening width': state.opening.w + 'mm',
        'Opening height': state.opening.h + 'mm',
        'Layout': lay ? lay.label : 'Custom',
        'Top shelf': state.internals.topShelf ? 'Yes' : 'No',
        'Mid support required': flags.midSupport ? 'Yes' : ''
      };
      for (var key in map) {
        var el = q(root, '[data-ma-field-label="' + key + '"]');
        if (el) el.value = map[key];
      }
      var msgEl = q(root, '[data-ma-role="message"]');
      if (msgEl) msgEl.value = describeFitout(state) + (userNotes ? '\n\n' + userNotes : '');
    }

    var notesInput = q(root, '[data-wfs-notes]');
    if (notesInput) {
      notesInput.addEventListener('input', function () {
        userNotes = notesInput.value;
        /* Carried in the design JSON too, so a CRM quote builder hydrating
           from `design` alone still gets what the customer typed. */
        state.notes = userNotes;
        syncLeadFields();
      });
    }

    /* Focus preservation — every mount rebuild does innerHTML = '', which
       throws focus to <body> and strands keyboard users. */
    function withFocusKept(fn) {
      var active = document.activeElement;
      var key = null;
      if (active && active !== document.body) {
        var group = active.closest ? active.closest('[data-wfs-mount]') : null;
        if (group) {
          var peers = qa(group, 'button, select, input');
          key = { mount: group.getAttribute('data-wfs-mount'), index: peers.indexOf(active) };
        }
      }
      fn();
      if (!key || key.index < 0) return;
      var host = root.querySelector('[data-wfs-mount="' + key.mount + '"]');
      if (!host) return;
      var again = qa(host, 'button, select, input');
      var target = again[key.index];
      if (target && typeof target.focus === 'function') target.focus();
    }

    /* Step 1 — opening size: range + number, kept in sync, mm suffix. */
    function sizeControl(labelText, conf, value, onChange) {
      var wrap = document.createElement('div');
      wrap.className = 'wfs-size';
      /* Namespaced by section id so two instances of this section on one
         page cannot produce duplicate control ids. */
      var id = 'wfs-' + sectionUid + '-size-' + labelText.toLowerCase().replace(/[^a-z]+/g, '');
      wrap.innerHTML =
        '<div class="wfs-size__head">' +
          '<label class="wfs-size__label" for="' + id + '-num">' + esc(labelText) + '</label>' +
          '<span class="wfs-size__value"><input class="wfs-size__num" id="' + id + '-num" type="number" inputmode="numeric" value="' + value + '" min="' + conf.min + '" max="' + conf.max + '" step="' + conf.step + '"><span class="wfs-size__unit">mm</span></span>' +
        '</div>' +
        '<input class="wfs-size__range" id="' + id + '-range" type="range" min="' + conf.min + '" max="' + conf.max + '" step="' + conf.step + '" value="' + value + '" aria-label="' + esc(labelText) + '">';

      var range = q(wrap, '.wfs-size__range');
      var num = q(wrap, '.wfs-size__num');
      var note = document.createElement('p');
      note.className = 'wfs-hint';
      note.hidden = true;

      function commit(raw, dragging) {
        var n = parseInt(raw, 10);
        if (isNaN(n)) { range.value = value; num.value = value; return; }
        var clamped = clamp(roundTo(n, conf.step), conf.min, conf.max);
        var msg = '';
        if (n > conf.max) msg = tpl(S.clampNote || '', { limit: conf.max });
        else if (n < conf.min) msg = tpl(S.clampNote || '', { limit: conf.min });
        note.textContent = msg;
        note.hidden = !msg;
        value = clamped;
        range.value = clamped;
        num.value = clamped;
        onChange(clamped, dragging);
      }
      /* Dragging repaints the drawing on every tick (that path is rAF
         gated) but only rebuilds the layout/parts mounts once the drag
         ends. Rebuilding both mounts on every input tick meant hundreds
         of synchronous teardowns across one slider drag. */
      range.addEventListener('input', function () { commit(range.value, true); });
      range.addEventListener('change', function () { commit(range.value); });
      num.addEventListener('change', function () { commit(num.value); });

      var box = document.createElement('div');
      box.appendChild(wrap);
      box.appendChild(note);
      return box;
    }

    function mountOpening() {
      var host = q(root, '[data-wfs-mount="opening"]');
      if (!host) return;
      host.innerHTML = '';
      host.appendChild(sizeControl(S.widthLabel || 'Width', cat.opening.width, state.opening.w, function (v, dragging) {
        state.opening.w = v;
        var snapped = reflowSections();
        pendingLayoutNote = snapped ? (S.layoutSnapNote || '') : '';
        scheduleRender();
        if (!dragging) withFocusKept(function () { mountLayout(); mountParts(); });
        announce('Opening now ' + v + ' millimetres wide');
      }));
      host.appendChild(sizeControl(S.heightLabel || 'Height', cat.opening.height, state.opening.h, function (v) {
        state.opening.h = v;
        scheduleRender();
        announce('Opening now ' + v + ' millimetres high');
      }));
    }

    /* Step 2 — layout picker (arrow-key navigable) + per-part rows. */
    function mountLayout() {
      var host = q(root, '[data-wfs-mount="layout"]');
      if (!host) return;
      host.innerHTML = '';

      var avail = GEOM.availableLayouts(state.opening.w, cat);
      var grid = document.createElement('div');
      grid.className = 'wfs-layouts';
      grid.setAttribute('role', 'radiogroup');
      grid.setAttribute('aria-label', S.layoutGroupLabel || 'Layout');

      avail.forEach(function (lay) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'wfs-layout';
        b.setAttribute('role', 'radio');
        var on = lay.id === state.internals.layout;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.tabIndex = on ? 0 : -1;
        b.innerHTML = '<span class="wfs-layout__name">' + esc(lay.label) + '</span><span class="wfs-layout__sub">' + esc(lay.sub) + '</span>';
        b.addEventListener('click', function () {
          state.internals.layout = lay.id;
          state.internals.sections = GEOM.realiseLayout(lay.recipe, state.opening.w, cat);
          ui.selected = -1;
          pendingLayoutNote = '';
          scheduleRender();
          withFocusKept(function () { mountLayout(); mountParts(); });
          announce(lay.label + ' layout selected');
        });
        grid.appendChild(b);
      });

      grid.addEventListener('keydown', function (e) {
        if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].indexOf(e.key) === -1) return;
        var cards = qa(grid, '[role="radio"]');
        var at = cards.indexOf(document.activeElement);
        if (at < 0) return;
        e.preventDefault();
        var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        var nx = (at + dir + cards.length) % cards.length;
        cards[nx].focus();
        cards[nx].click();
      });
      (function () {
        var cards = qa(grid, '[role="radio"]');
        if (cards.length && !cards.some(function (c) { return c.tabIndex === 0; })) cards[0].tabIndex = 0;
      })();

      host.appendChild(grid);

      if (pendingLayoutNote) {
        var note = document.createElement('p');
        note.className = 'wfs-hint';
        note.textContent = pendingLayoutNote;
        host.appendChild(note);
        pendingLayoutNote = '';
      }
    }

    function partRowDetail(sec) {
      if (sec.t === 'drawer') return sec.drawers + ' drawers, ' + (sec.hangAbove !== false ? 'hanging above' : 'shelves above');
      if (sec.t === 'shelf') return sec.shelves + ' shelves';
      return sec.w + 'mm wide';
    }

    /* Shared <select> builder for the drawer/shelf count pickers. */
    function countSelect(ariaLabel, options, current, suffix, onPick) {
      var sel = document.createElement('select');
      sel.className = 'wfs-select';
      sel.setAttribute('aria-label', ariaLabel);
      options.forEach(function (n) {
        var opt = document.createElement('option');
        opt.value = n;
        opt.textContent = n + ' ' + suffix;
        if (n === current) opt.selected = true;
        sel.appendChild(opt);
      });
      sel.addEventListener('change', function () { onPick(parseInt(sel.value, 10)); });
      return sel;
    }

    function mountParts() {
      var host = q(root, '[data-wfs-mount="parts"]');
      if (!host) return;
      host.innerHTML = '';

      var list = document.createElement('ul');
      list.className = 'wfs-parts';

      state.internals.sections.forEach(function (sec, i) {
        var meta = cat.sections[sec.t];
        if (!meta) return;
        var li = document.createElement('li');
        li.className = 'wfs-part';

        var row = document.createElement('div');
        row.className = 'wfs-part__row';
        row.innerHTML =
          '<span class="wfs-part__name">Part ' + (i + 1) + ' &middot; ' + esc(meta.label) + '</span>' +
          '<span class="wfs-part__detail">' + esc(partRowDetail(sec)) + '</span>';
        li.appendChild(row);

        var ctrls = document.createElement('div');
        ctrls.className = 'wfs-part__ctrls';

        var typeSel = document.createElement('select');
        typeSel.className = 'wfs-select';
        typeSel.setAttribute('aria-label', 'Part ' + (i + 1) + ' type');
        Object.keys(cat.sections).forEach(function (k) {
          var opt = document.createElement('option');
          opt.value = k;
          opt.textContent = cat.sections[k].label;
          if (k === sec.t) opt.selected = true;
          typeSel.appendChild(opt);
        });
        typeSel.addEventListener('change', function () {
          var t = typeSel.value;
          var nm = cat.sections[t];
          var next = { t: t, w: clamp(sec.w, nm.min, nm.max) };
          if (t === 'drawer') {
            next.drawers = nm.params.drawers.default;
            next.hangAbove = nm.params.hangAbove.default;
          }
          if (t === 'shelf') next.shelves = nm.params.shelves.default;
          state.internals.sections[i] = next;
          normaliseSections(state.internals.sections, state.opening.w);
          state.internals.layout = null;
          scheduleRender();
          withFocusKept(function () { mountParts(); });
          announce(nm.label + ' selected for part ' + (i + 1));
        });
        ctrls.appendChild(typeSel);

        if (sec.t === 'drawer') {
          ctrls.appendChild(countSelect('Part ' + (i + 1) + ' drawer count', meta.params.drawers.options, sec.drawers, 'drawers', function (n) {
            state.internals.sections[i].drawers = n;
            scheduleRender();
            withFocusKept(function () { mountParts(); });
          }));

          var haLabel = document.createElement('label');
          haLabel.className = 'wfs-toggle wfs-toggle--inline';
          haLabel.innerHTML = '<input type="checkbox"' + (sec.hangAbove !== false ? ' checked' : '') + '><span>' + esc(meta.params.hangAbove.labels['true']) + '</span>';
          q(haLabel, 'input').addEventListener('change', function (e) {
            state.internals.sections[i].hangAbove = e.target.checked;
            scheduleRender();
            withFocusKept(function () { mountParts(); });
          });
          ctrls.appendChild(haLabel);
        }

        if (sec.t === 'shelf') {
          ctrls.appendChild(countSelect('Part ' + (i + 1) + ' shelf count', meta.params.shelves.options, sec.shelves, 'shelves', function (n) {
            state.internals.sections[i].shelves = n;
            scheduleRender();
            withFocusKept(function () { mountParts(); });
          }));
        }

        li.appendChild(ctrls);
        list.appendChild(li);
      });

      host.appendChild(list);

      var tsWrap = document.createElement('label');
      tsWrap.className = 'wfs-toggle';
      tsWrap.innerHTML = '<input type="checkbox"' + (state.internals.topShelf ? ' checked' : '') + '><span>' + esc(cat.topShelf.label) + '</span>';
      q(tsWrap, 'input').addEventListener('change', function (e) {
        state.internals.topShelf = e.target.checked;
        scheduleRender();
      });
      host.appendChild(tsWrap);
    }

    /* Boot */
    mountOpening();
    mountLayout();
    mountParts();
    paint();
  }

  function boot() {
    qa(document, '[data-wfs-root]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
